import React, { useState } from "react";
import { CheckCircle2, Loader2, PartyPopper, Briefcase, Cake, GraduationCap } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { IMAGES, PRIVATE_EVENT_TYPES } from "@/lib/content";

const HIGHLIGHTS = [
  { icon: Cake, title: "Birthday Parties", desc: "Reserved sections, bottle sparklers & a night they'll never forget." },
  { icon: Briefcase, title: "Corporate & Networking", desc: "Elevated mixers and happy hours for teams and clients." },
  { icon: GraduationCap, title: "Graduations", desc: "Celebrate the milestone with the crew in style." },
  { icon: PartyPopper, title: "Bachelor / Bachelorette", desc: "The ultimate send-off with VIP treatment all night." },
];

export default function PrivateEvents() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: "Birthday Party", date: "", guests: 20, message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", { ...form, guests: Number(form.guests) });
      setDone(true);
      toast.success("Inquiry sent! Our events team will reach out within 24 hours.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-neutral-100 border border-black/12 rounded-xl px-4 py-3 text-neutral-900 placeholder:text-neutral-300 outline-none focus:border-neutral-900 transition-colors";

  return (
    <>
      <Seo title="Private Events & Birthday Venue DC" description="Host private events at ChiCha Lounge DC — birthdays, corporate mixers, graduations and bachelor parties. Request a quote for U Street's premier venue." path="/private-events" image={IMAGES.crowd} />
      <PageHero overline="Celebrate With Us" title="Private Events" subtitle="From intimate birthdays to full buyouts — DC's most memorable private venue on U Street." image={IMAGES.crowd} />

      <section className="pb-16 bg-transparent">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {HIGHLIGHTS.map((h) => {
              const Icon = h.icon;
              return (
                <StaggerItem key={h.title}>
                  <div className="card-lux p-7 h-full">
                    <Icon size={26} className="text-neutral-900" />
                    <h3 className="font-serif text-2xl text-neutral-900 mt-4">{h.title}</h3>
                    <p className="text-neutral-500 text-sm mt-2 leading-relaxed">{h.desc}</p>
                  </div>
                </StaggerItem>
              );
            })}
          </Stagger>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <Reveal>
              <span className="overline">Request a Quote</span>
              <h2 className="font-serif text-4xl md:text-5xl text-neutral-900 mt-3 leading-tight">Let's Plan Your <span className="text-accent">Event</span></h2>
              <p className="text-neutral-500 text-lg mt-5 leading-relaxed">Tell us about your celebration and our events team will craft a custom package — sections, bottles, hookah, food and more.</p>
              <img src={IMAGES.marbleBar} alt="Private events at ChiCha Lounge DC" loading="lazy" className="rounded-2xl w-full h-[280px] object-cover mt-8" />
            </Reveal>

            {done ? (
              <Reveal delay={0.1}>
                <div className="card-lux p-12 text-center">
                  <CheckCircle2 size={56} className="text-neutral-900 mx-auto mb-6" />
                  <h3 className="font-serif text-3xl text-neutral-900">Inquiry Received</h3>
                  <p className="text-neutral-500 mt-4">Thank you, {form.name.split(" ")[0]}! Our events team will contact you within 24 hours.</p>
                </div>
              </Reveal>
            ) : (
              <form onSubmit={submit} className="card-lux p-8 space-y-5">
                <div className="grid md:grid-cols-2 gap-5">
                  <div><label className="overline block mb-2">Name</label><input data-testid="inq-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={field} placeholder="Your name" /></div>
                  <div><label className="overline block mb-2">Phone</label><input data-testid="inq-phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={field} placeholder="(202) 555-0100" /></div>
                </div>
                <div><label className="overline block mb-2">Email</label><input data-testid="inq-email" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={field} placeholder="you@email.com" /></div>
                <div className="grid md:grid-cols-3 gap-5">
                  <div className="md:col-span-1"><label className="overline block mb-2">Event Type</label>
                    <select data-testid="inq-type" value={form.event_type} onChange={(e) => set("event_type", e.target.value)} className={field}>
                      {PRIVATE_EVENT_TYPES.map((t) => <option key={t} className="bg-white">{t}</option>)}
                    </select>
                  </div>
                  <div><label className="overline block mb-2">Preferred Date</label><input data-testid="inq-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={field} /></div>
                  <div><label className="overline block mb-2">Guests</label><input data-testid="inq-guests" type="number" min="1" value={form.guests} onChange={(e) => set("guests", e.target.value)} className={field} /></div>
                </div>
                <div><label className="overline block mb-2">Tell us more</label><textarea data-testid="inq-message" rows="4" value={form.message} onChange={(e) => set("message", e.target.value)} className={field} placeholder="Occasion, vibe, budget, must-haves…" /></div>
                <button data-testid="inq-submit" disabled={loading} className="btn-gold w-full justify-center">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : null} {loading ? "Sending…" : "Request Quote"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
